"use client";

import { BlurredValue } from "@/components/blurred-value";
import { useConvertedPrice } from "@/hooks/use-converted-price";
import { useLivePrices } from "@/hooks/use-live-prices";
import { cn } from "@/lib/utils";
import { useUserCurrency } from "@/providers/user-preferences-provider";
import { Asset } from "@/services/assets/schema";
import { isNil } from "@/utils/is-nil";
import Decimal from "decimal.js";
import { usePathname } from "next/navigation";
import { useOptimistic } from "react";
import { OpenClosePrice } from "./open-close-price";
import { PortfolioValueVisibility } from "./portfolio-value-visibility";

interface Props {
  readonly portfolioValue: number;
  readonly balance: number;
  readonly assets: Asset[];
  readonly investedCapital: number;
  readonly showLabel?: boolean;
  readonly portfolioId: string;
  readonly isUserPortfolio?: boolean;
  readonly isValueVisible?: boolean;
  readonly showValueInBtc?: boolean;
}

export function PortfolioValue({
  portfolioValue,
  balance,
  assets,
  portfolioId,
  showLabel = true,
  isUserPortfolio = false,
  isValueVisible = false,
  showValueInBtc = false,
}: Props) {
  const pathname = usePathname();
  const { coingeckoLivePrices, binanceLivePrices } = useLivePrices(assets);
  const [optimistic, updateValueVisibility] = useOptimistic(
    { isValueVisible, checkOptimistic: false },
    (_state, newState: boolean) => {
      return {
        isValueVisible: newState,
        checkOptimistic: true,
      };
    },
  );

  const isVisible = optimistic.checkOptimistic
    ? optimistic.isValueVisible
    : isValueVisible;

  const [portfolioValueOpen, portfolioValueClose] =
    assets.length > 0
      ? assets.reduce(
          (value, { transactionsTotal, ...asset }) => {
            const binanceEntry = binanceLivePrices[asset.symbol];
            const assetId = asset.thirdPartyIds?.coingecko ?? asset.symbol;
            const coinGeckoEntry = coingeckoLivePrices?.[assetId];

            const amount = new Decimal(transactionsTotal.amount).minus(
              transactionsTotal.fee,
            );

            if (!isNil(binanceEntry?.open)) {
              value[0] += amount.mul(binanceEntry.open).toNumber();
              value[1] += amount.mul(binanceEntry.close).toNumber();
            } else if (coinGeckoEntry) {
              value[0] += amount.mul(coinGeckoEntry.price).toNumber();
              value[1] += amount.mul(coinGeckoEntry.price).toNumber();
            }

            return value;
          },
          [balance, balance],
        )
      : [portfolioValue, portfolioValue];

  const currency = useUserCurrency();
  const otherCurrency = currency !== "BTC" ? "BTC" : "USD";
  const convertedPortfolioValueOpen = useConvertedPrice(
    portfolioValueOpen,
    otherCurrency,
    currency,
  );
  const convertedPortfolioValueClose = useConvertedPrice(
    portfolioValueClose,
    otherCurrency,
    currency,
  );

  if (showLabel) {
    return (
      <div className="grid w-full grid-cols-4 gap-1">
        {/* <p className="text-md col-span-2  self-center font-semibold">
          Invested Capital:
        </p>
        <BlurredValue
          isVisible={isVisible}
          value={`$ ${parseNumericObject(investedCapital)}`}
        /> */}
        {/* <p className="text-md col-span-2  self-center font-semibold">
          Cash:
        </p> */}
        {/* <BlurredValue
          isVisible={isVisible}
          value={`$ ${parseNumericObject(
            balanceNonNegative,
            balanceNonNegative > 1
              ? 2
              : getNumberOfFractionDigits(balanceNonNegative) || 2,
          )}`}
          asHtml
        /> */}
        <p className="text-md col-span-2 self-center font-semibold">
          Portfolio Value:
        </p>

        <BlurredValue
          isVisible={isVisible}
          value={portfolioValueClose}
          className="text-md col-span-1 self-center font-semibold"
        >
          <OpenClosePrice
            open={portfolioValueOpen}
            close={portfolioValueClose}
            className={cn("text-md self-center font-semibold")}
          />
        </BlurredValue>

        {isUserPortfolio ? (
          <div className="pl-2">
            <PortfolioValueVisibility
              isValueVisible={isVisible}
              portfolioId={portfolioId}
              optimisticFn={updateValueVisibility}
              path={pathname}
            />
          </div>
        ) : null}
      </div>
    );
  } else {
    return (
      <>
        <OpenClosePrice
          open={portfolioValueOpen}
          close={portfolioValueClose}
          className={cn("text-md", "font-semibold", "")}
          // Hide fractions for HNWI portfolio values
          maxDigits={portfolioValueOpen > 10_000 ? 0 : undefined}
        />
        {showValueInBtc ? (
          <OpenClosePrice
            open={convertedPortfolioValueOpen.price}
            close={convertedPortfolioValueClose.price}
            className={cn("pl-1 !text-base")}
            currency={convertedPortfolioValueClose.meta.symbol}
          />
        ) : null}
      </>
    );
  }
}
